/**
 * Who is talking right now.
 *
 * A call where you cannot tell who is speaking is the thing everyone complains
 * about on a conference bridge: four names on screen and no idea which one to
 * answer. WebRTC does not tell us, so this measures it — the loudness of each
 * stream, smoothed enough that a cough does not light someone up and a pause
 * for breath does not put them out.
 *
 * One AudioContext, one analyser per stream, one polling loop for all of them.
 */

/** Loudness (0-1 RMS) a stream has to clear to count as speech. */
const SPEAKING_THRESHOLD = 0.02;
/** How long quiet has to last before someone stops being "speaking". */
const RELEASE_MS = 350;
const POLL_MS = 120;

interface Watched {
  stream: MediaStream;
  analyser: AnalyserNode;
  source: MediaStreamAudioSourceNode;
  data: Uint8Array<ArrayBuffer>;
  quietSince: number | null;
  speaking: boolean;
}

export class SpeakingDetector {
  private context: AudioContext | null = null;
  private readonly watched = new Map<string, Watched>();
  private timer: number | null = null;

  constructor(private readonly onChange: (speaking: Set<string>) => void) {}

  /** Replaces the whole set of streams being watched, keyed by user id. */
  sync(streams: Map<string, MediaStream>): void {
    for (const [id, watched] of this.watched) {
      if (streams.get(id) !== watched.stream) {
        watched.source.disconnect();
        this.watched.delete(id);
      }
    }

    for (const [id, stream] of streams) {
      if (this.watched.has(id)) continue;
      if (stream.getAudioTracks().length === 0) continue;
      const context = this.ensureContext();
      if (!context) return;
      try {
        const source = context.createMediaStreamSource(stream);
        const analyser = context.createAnalyser();
        // Small window: this is a loudness meter, not a spectrogram, and a
        // large FFT would smear the start of a word across several polls.
        analyser.fftSize = 512;
        source.connect(analyser);
        this.watched.set(id, {
          stream,
          analyser,
          source,
          data: new Uint8Array(new ArrayBuffer(analyser.fftSize)),
          quietSince: null,
          speaking: false,
        });
      } catch {
        // A stream with no audio, or a context the browser refused.
      }
    }

    if (this.watched.size > 0) this.start();
    else this.stop();
  }

  private ensureContext(): AudioContext | null {
    if (this.context) return this.context;
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    this.context = new Ctor();
    return this.context;
  }

  private start(): void {
    if (this.timer !== null) return;
    this.timer = window.setInterval(() => this.poll(), POLL_MS);
  }

  private stop(): void {
    if (this.timer === null) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  private poll(): void {
    const now = Date.now();
    let changed = false;

    for (const watched of this.watched.values()) {
      watched.analyser.getByteTimeDomainData(watched.data);
      let sum = 0;
      for (const sample of watched.data) {
        const centred = (sample - 128) / 128;
        sum += centred * centred;
      }
      const level = Math.sqrt(sum / watched.data.length);

      if (level >= SPEAKING_THRESHOLD) {
        watched.quietSince = null;
        if (!watched.speaking) {
          watched.speaking = true;
          changed = true;
        }
        continue;
      }

      // Hold on through the gaps between words, or a sentence looks like ten.
      watched.quietSince ??= now;
      if (watched.speaking && now - watched.quietSince > RELEASE_MS) {
        watched.speaking = false;
        changed = true;
      }
    }

    if (!changed) return;
    const speaking = new Set<string>();
    for (const [id, watched] of this.watched) if (watched.speaking) speaking.add(id);
    this.onChange(speaking);
  }

  destroy(): void {
    this.stop();
    for (const watched of this.watched.values()) watched.source.disconnect();
    this.watched.clear();
    void this.context?.close().catch(() => undefined);
    this.context = null;
  }
}
